import {createSlice, PayloadAction} from "@reduxjs/toolkit";
import {
  IMySongsLibraryResponseLibrarySong,
  IMySongsLibraryResponseUserLibraryLesson
} from "../../../../client/models/mySongsLibrary/IMySongsLibraryResponse";
import {isDefined} from "../../../../util/is-type";
import {store} from "../../../../redux/store";

export interface IMySongsLibraryFullLibrarySongLesson {
  difficultyId: number;
  arrangementTypeId: number;
  dateAdded: number;
  hasVideo: boolean;
  popularity: number;
}

export interface IMySongsLibraryFullLibrarySong {
  songId: number;
  thumbnailUrl: string;
  name: string;
  artistName: string;
  hasVideo: boolean;
  genreId: number;
  lessons: IMySongsLibraryFullLibrarySongLesson[];
}

export interface IMySongsLibraryUserLibraryLesson {
  lessonId: number;
  thumbnailUrl: string;
  name: string;
  difficultyIdentifier: "rookie" | "intermediate" | "advanced";
  difficultyName: string;
  hasVideo: boolean;
  artistName: string;
  songName: string;
  popularity: number;

  // User-specific data
  isFavorited: boolean;
  quantityStars: number;

  // For sorting/filtering
  difficultyId: number;
  arrangementTypeId: number;
  genreId: number;
  dateAdded: number;
}

export interface IMySongsLibrary {
  loadedAt?: number;
  fullLibrary?: IMySongsLibraryFullLibrarySong[];
  userLibrary?: IMySongsLibraryUserLibraryLesson[];
}

export interface IMySongsLibraryStore extends StoreWithRequestStatus<IMySongsLibrary> {}

export interface IMySongsLibraryStoreLoadPayload {
  // full playground library songs
  fullLibrary?: IMySongsLibraryResponseLibrarySong[];
  // user library of lessons
  userLibrary?: IMySongsLibraryResponseUserLibraryLesson[];
}

const initialState: IMySongsLibraryStore = {};

/**
 * This slice represents part of an API response,
 * with some additional data about the status and time it was loaded.
 * @see IMySongsLibraryResponse
 */
export const mySongsLibrarySlice = createSlice({
  name: `mySongsLibrary`,
  initialState,

  reducers: (create) => ({
    requestStatusReducer: create.reducer(
      (store: IMySongsLibraryStore, action: PayloadAction<"loading" | "error" | "completed">) => {
        store.requestStatus = action.payload;
      }
    ),

    fillWithDataReducer: create.reducer(
      (store: IMySongsLibraryStore, action: PayloadAction<IMySongsLibraryStoreLoadPayload>) => {
        store.loadedAt = Date.now(); // For debugging purposes
        store.fullLibrary = action.payload.fullLibrary;
        store.userLibrary = action.payload.userLibrary;
      }
    ),

    resetReducer: create.reducer((_store: IMySongsLibraryStore) => {
      return {
        ...initialState,
        loadedAt: 0, // Why is this set to something other than the initial state?
      };
    }),

    userLibraryLessonFavoriteReducer: create.reducer(
      (store: IMySongsLibraryStore, action: PayloadAction<{lessonId: number; isFavorited: boolean}>) => {
        const {lessonId, isFavorited} = action.payload;

        const isLessonAvailableInStore = isDefined(
          store
            .userLibrary
            ?.find(({lessonId: id})=> id === lessonId)
        );

        if (!isLessonAvailableInStore) {
          // no lesson to update, return unchanged
          return store;
        }

        const updatedUserLibrary = [
          // spread to prevent modifying state
          ...(store.userLibrary ?? []),
        ]
          .map((userLibraryLesson) => {
            if (userLibraryLesson.lessonId !== lessonId) {
              // nothing to update on this lesson
              return userLibraryLesson;
            }

            return {
              ...userLibraryLesson,
              isFavorited,
            };
          });

        return {
          ...store,
          userLibrary: updatedUserLibrary,
        };
      }
    ),
  }),
  selectors: {
    requestStatusSelector: (store: IMySongsLibraryStore) => store.requestStatus,
    fullLibrarySelector: (store: IMySongsLibraryStore) => store.fullLibrary,
    userLibrarySelector: (store: IMySongsLibraryStore) => store.userLibrary,
  }
});

export const mySongsLibraryStore = {
  // Setters - things that modify state
  setRequestStatus: (status: "loading" | "error" | "completed") => { store.dispatch(mySongsLibrarySlice.actions.requestStatusReducer(status)); },
  fillWithData: ({fullLibrary, userLibrary}: IMySongsLibraryStoreLoadPayload) => { store.dispatch(mySongsLibrarySlice.actions.fillWithDataReducer({fullLibrary, userLibrary})); },
  reset: () => { store.dispatch(mySongsLibrarySlice.actions.resetReducer()); },
  setUserLibraryLessonFavorite: ({lessonId, isFavorited}: {lessonId: number; isFavorited: boolean}) => { store.dispatch((mySongsLibrarySlice.actions.userLibraryLessonFavoriteReducer({ lessonId, isFavorited }))); },
  // Getters
  getIsRequestReadyToSend: (): boolean => typeof (mySongsLibrarySlice.selectors.requestStatusSelector(store.getState())) === "undefined",
  getIsRequestCompleted: (): boolean => (mySongsLibrarySlice.selectors.requestStatusSelector(store.getState()) ?? "") === "completed",
  getIsRequestErrored:   (): boolean => (mySongsLibrarySlice.selectors.requestStatusSelector(store.getState()) ?? "") === "error",
  getIsRequestLoading:   (): boolean => (mySongsLibrarySlice.selectors.requestStatusSelector(store.getState()) ?? "") === "loading",
  getFullLibrary: (): IMySongsLibraryFullLibrarySong[] => mySongsLibrarySlice.selectors.fullLibrarySelector(store.getState()) ?? [],
  getUserLibrary: (): IMySongsLibraryUserLibraryLesson[] => mySongsLibrarySlice.selectors.userLibrarySelector(store.getState()) ?? [],
};

export const mySongsLibraryReducer = mySongsLibrarySlice.reducer;
